(function(angular, _) {
    angular.module('wtApp.dataService').factory('ReportGenerationResult', ['$injector', function($injector) {
        const Monads = $injector.get('Monads');
        const Variants = $injector.get('Variants');
        const cohortUtils = $injector.get('cohortUtils');
        const arrangeTypeUtils = $injector.get('arrangeTypeUtils');
        const viewModeComparison = $injector.get('viewModeComparison');
        const reportGenerationResultUtils = $injector.get('reportGenerationResultUtils');

        /**
         * @ngdoc object
         * @name ReportGenerationResult
         * @description Report generation result instance constructor. Provides a unified interface to query the result set.
         */
        return class ReportGenerationResult {
            static get MANDATORY_FIELDS() {
                return [
                    'calculatedLayer',
                    'calculationTime',
                    'columns',
                    'shownRowCount',
                    'start',
                    'stop',
                    'totalRowCount',
                    'warnings'
                ];
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult.isValid
             * @description Validate an object against the MANDATORY_FIELDS of a report generation result object
             * @param {Object} iResult
             * @returns {*}
             */
            static isValid(iResult) {
                return reportGenerationResultUtils.isValidReportGenerationResult(iResult);
            }

            constructor(result) {
                this.result = result;
                this.isValidResult = ReportGenerationResult.isValid(result);
                this.processedResponse = null;
                this.variant = null;
                this.viewMode = null;
                this.responseForChart = null;
                this.forceList = false;
                this.rowsCountPerPage = null;
                this.arrangeTypes = null;
                this.dimensions = null;
                this.metrics = null;

                // Determine if the response is a cohort response
                this.isCohort_ = cohortUtils.isCohortResponse(
                    _.filter(this.getColumns(), {metaType: 'NORMAL'})
                );
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#setForceList
             * @description Sets the forceList property
             * @param   {boolean} forceList
             * @returns {ReportGenerationResult}
             */
            setForceList(forceList) {
                this.forceList = forceList;
                return this;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#setViewMode
             * @description Sets the viewMode property
             * @param   {Object} viewMode
             * @returns {ReportGenerationResult}
             */
            setViewMode(viewMode) {
                this.viewMode = viewMode;
                return this;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getViewMode
             * @description Returns the viewMode property
             * @returns {null|string}
             */
            getViewMode() {
                return this.viewMode;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getColumns
             * @description Returns the columns list of the current result.
             * @returns {Array}
             */
            getColumns() {
                if (!this.isValidResult) {
                    return [];
                }
                return this.result.columns;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getArrangeTypes
             * @description Initializes and returns the arrange types found in the columns of a result
             * @returns {Array}
             */
            getArrangeTypes() {
                if (!this.isValidResult) {
                    this.arrangeTypes = [];
                }

                if (_.isArray(this.arrangeTypes)) {
                    return this.arrangeTypes;
                }

                // Consider only NORMAL columns. All other metaTypes include meta information
                const normalColumns = _.filter(this.getColumns(), {metaType: 'NORMAL'});

                this.arrangeTypes = arrangeTypeUtils.getArrangeTypesFromResponse(normalColumns);

                return this.arrangeTypes;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getDimensions
             * @description Initializes and returns the dimensions found in the columns of a result
             * @returns {Array|*} A list of complete dimension configuration objects
             */
            getDimensions() {
                if (!this.isValidResult) {
                    this.dimensions = [];
                }

                if (_.isArray(this.dimensions)) {
                    return this.dimensions;
                }

                const layer = this.layer_();

                this.dimensions = _.chain(this.getArrangeTypes())
                    .map((arrangeType) => Monads.layer(layer).monad(arrangeType))
                    .filter({dimension: true})
                    .value();

                return this.dimensions;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getMetrics
             * @description Initializes and returns the metrics found in the columns of a result
             * @returns {Array<Object>} A list of complete metric configuration objects
             */
            getMetrics() {
                if (!this.isValidResult) {
                    this.metrics = [];
                }

                if (_.isArray(this.metrics)) {
                    return this.metrics;
                }

                const layer = this.layer_();

                this.metrics = _.chain(this.getArrangeTypes())
                    .map((arrangeType) => Monads.layer(layer).monad(arrangeType))
                    .filter({metric: true})
                    .value();

                return this.metrics;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getElementColumns
             * @description Returns a list of complete elements (dimension & metric - configuration objects)
             * @returns {Array}
             */
            getElementColumns() {
                const metrics = this.getMetrics();
                const dimensions = this.getDimensions();
                return dimensions.concat(metrics);
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#asList
             * @description Proxies the asList method
             * @param {Object} options
             * @returns {*}
             */
            asList(options) {
                return reportGenerationResultUtils.asList(this.result, options, this.getDimensions().length);
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#asPivot
             * @description Proxies the asPivot method
             * @returns {*}
             */
            asPivot() {
                return reportGenerationResultUtils.asPivot(this.result, this.getDimensions().length);
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#setVariant
             * @description Resolves the variant of an analysis based on the number of dimensions, its preset variant and optionally the force list flag
             * @param   {string} variant
             * @param   {boolean} forceList
             * @returns {ReportGenerationResult}
             */
            setVariant(variant, forceList) {
                this.variant = Variants.resolve(this.getDimensions().length, variant, forceList);
                return this;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getVariant
             * @description Resolves the variant of an analysis based on the number of dimensions, its preset variant and optionally the force list flag
             * @returns {null|string}
             */
            getVariant() {
                return this.variant;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getProcessedResponse
             * @description Will return the transformed data
             * @returns {null|*}
             */
            getProcessedResponse() {
                return this.processedResponse;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getProcessedResponseForChart
             * @description Returns the processed data set specially constructed for the charts module - if any. Always null for list variants.
             * @returns {null|*}
             */
            getProcessedResponseForChart() {
                return this.responseForChart;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#isCohort
             * @description Determines if the response is a cohort analysis response
             * @returns {boolean|*}
             */
            isCohort() {
                return this.isCohort_;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#isTimeBasedComparison_
             * @description Determines if the response is a time comparison analysis response
             * @returns {boolean}
             * @private
             */
            isTimeBasedComparison_() {
                return viewModeComparison.isTimeBasedComparison(this.getViewMode());
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getRowsPerPage
             * @description Computes the number of rows to be displayd based on the result
             * @returns {*}
             */
            getRowsPerPage() {
                if (this.rowsCountPerPage) {
                    return this.rowsCountPerPage;
                }

                const hasEmptySum = _.chain(this.processedResponse).get('dataTableSum').isEmpty().value();

                if (this.forceList || hasEmptySum) {
                    return _.get(this.result, 'shownRowCount');
                }
                // Number of rows minus the sum row
                return _.get(this.result, 'shownRowCount') - 1;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#canNullify
             * @description Determines if the response must unstringify null values or not
             * @returns {boolean}
             */
            canNullify() {
                return this.isCohort() || this.isTimeBasedComparison_();
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#normalizeNulls
             * @description Normalizes 'null' based on type. Will iterate over the data of the columns of the response
             * and convert 'null' and null to '0' for non cohorts requests and to regular null for cohort
             * requests.
             */
            normalizeNulls() {
                const layer = this.layer_();

                _.chain(this.getColumns())
                    .filter((col) => {
                        // Transform only the data columns. Not columns containing meta info
                        // i.e. with metaType ORDER etc.
                        if (_.get(col, 'metaType') !== 'NORMAL') {
                            return false;
                        }
                        const arrangeType = arrangeTypeUtils.getArrangeTypeFromAlias(_.get(col, 'alias'));
                        // TODO
                        const monad = Monads.layer(layer).monad(arrangeType);
                        // Normalize only metrics. Dimensions should stay as they are.
                        return _.get(monad, 'metric', false);
                    })
                    .forEach((iColumn) => {
                        _.chain(iColumn)
                            .get('data')
                            .forEach((iValue, iNdex) => {
                                if (iValue === 'null' || _.isNull(iValue)) {
                                    iColumn.data[iNdex] = this.canNullify() ? null : '0';
                                }
                            })
                            .value();
                    })

                    .value();
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#processResponse
             * @description Will process a report generation result - set the variant, forceList and transform the data accordingly
             * @param {Object} iResult
             * @param {boolean} iSkipNormalize - Skip data normalize or not
             * @returns {ReportGenerationResult}
             */
            processResponse(iResult, iSkipNormalize) {
                if (!iSkipNormalize) {
                    this.normalizeNulls();
                }

                switch (this.variant) {
                    case 'PIVOT':
                        this.processedResponse = this.asPivot();
                        // Create a flattend version of the data for the chart
                        this.responseForChart = this.getPivotDataForChart_();
                        break;
                    case 'PIVOT_AS_LIST':
                        this.processedResponse = this.asList({forceList: this.forceList});
                        this.responseForChart = this.getPivotAsListDataForChart_();
                        break;
                    case 'LIST':
                        this.processedResponse = this.asList({forceList: this.forceList});
                        this.responseForChart = null;
                        break;
                }
                // Set the rows per page
                this.rowsCountPerPage = this.getRowsPerPage();

                this.processViewMode_(iResult, this.getColumns());

                return this;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#reorderColumns
             * @description Will reorder the data columns of the result returned from the report generation response
             * @param  {Array} orderedAliases A list of the column aliases in the new order
             * @returns {ReportGenerationResult}
             */
            reorderColumns(orderedAliases) {
                const columns = this.getColumns();
                const dataColumns = _.filter(columns, (column) => _.get(column, 'metaType') === 'NORMAL');
                const metaColumns = _.filter(columns, (column) => _.get(column, 'metaType') !== 'NORMAL');
                const newColumns = _.chain(orderedAliases)
                    .map((alias) => _.find(dataColumns, {alias}))
                    .concat(metaColumns)
                    .value();

                this.setColumns_(newColumns);

                return this;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getResult
             * @description Returns the result object as returned from the report generation plugin
             * @returns {Object}
             */
            getResult() {
                return _.cloneDeep(this.result);
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#layer_
             * @description Returns the final calculated layer the response was calculated on in the main server.
             * @returns {any}
             * @private
             */
            layer_() {
                return _.get(this, 'result.calculatedLayer');
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#setColumns_
             * @description Sets the columns of the result of this instance
             * @param {Object} columns
             * @returns {ReportGenerationResult}
             * @private
             */
            setColumns_(columns) {
                _.set(this, 'result.columns', columns);

                return this;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getPivotDataForChart_
             * @description Generates a flattened version of the data table data used by the charts.
             * Flattened meaning, only the first dimension is kept, all other dimensions are dropped and the metrics are flattened.
             * Te deep nesting used in data table is unmarshalled.
             * Applicable only in case of PIVOT variant.
             * @returns {Array}
             * @private
             */
            getPivotDataForChart_() {
                const chartData = [];
                const numberOfColumns = this.getElementColumns().length;
                const firstMetricIndex = this.getDimensions().length;

                _.chain(this.processedResponse)
                    .get('dataTable')
                    .forEach((row) => {
                        const sumRow = _.flattenDeep(_.last(row));
                        const rowTitle = sumRow[0];
                        const newRow = [rowTitle];
                        if (rowTitle) {
                            for (let metricIx = firstMetricIndex; metricIx < numberOfColumns; metricIx++) {
                                newRow.push(sumRow[metricIx]);
                            }
                            chartData.push(newRow);
                        }
                    })
                    .value();

                return chartData;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#getPivotAsListDataForChart_
             * @description Will generate a variant of the marshalled data for pivot as list with only the values of the first dimension
             * and all the values of the metrics, needed for the chart module.
             * @returns {Array}
             * @private
             */
            getPivotAsListDataForChart_() {
                const chartData = [];
                const numberOfColumns = this.getElementColumns().length;
                const firstMetricIndex = this.getDimensions().length;

                _.chain(this.processedResponse)
                    .get('dataTable')
                    .forEach((row) => {
                        const newRow = [row[0]];
                        for (let metricIx = firstMetricIndex; metricIx < numberOfColumns; metricIx++) {
                            newRow.push(row[metricIx]);
                        }
                        chartData.push(newRow);
                    })
                    .value();

                return chartData;
            }

            /**
             * @ngdoc method
             * @name ReportGenerationResult#processViewMode_
             * @description Will modify the processedResponse member according to the needs of the viewMode. Eg. for time series an additional
             * member dataTableColumnTitles is added.
             * @param {Object} iResult
             * @param {Object} iColumns
             * @private
             */
            processViewMode_(iResult, iColumns) {
                if (!_.isObject(this.processedResponse)) {
                    return;
                }

                // Add the column titles if the table is in viewMode time series
                if (_.get(iResult, 'viewMode', 'DEFAULT') === 'TIME_SERIES') {
                    this.processedResponse.dataTableColumnTitles = _.chain(iColumns)
                        .filter({
                            metaType: 'NORMAL'
                        })
                        .map((iColumn) => {
                            return _.chain(iColumn)
                                .pick(['alias', 'title'])
                                .values()
                                .value();
                        })
                        .fromPairs()
                        .value();
                }
            }
        };
    }]);
})(window.angular, window._);
